/**
 * 协议政策 API hooks
 *
 * 后端:
 * - GET  /app/system/policy/{type}      单条协议正文 (bizType 必传; 允许匿名, 用于登录前阅读)
 * - GET  /app/system/policy/required    当前用户必须同意的协议清单 (含同意状态; 需登录)
 * - POST /app/system/policy/accept      批量提交同意 (幂等)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/http'
import type {
  AppPolicyAcceptBo,
  AppPolicyAcceptItemBo,
  AppPolicyContentVo,
  AppPolicyRequiredItemVo,
} from '@/types/api'
import { POLICY_BIZ_TYPE } from './constants'

export type { AppPolicyContentVo, AppPolicyRequiredItemVo }

const LOGIN_POLICY_CONSENT_KEY = 'legal-web:login-policy-consent'

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** 登录弹窗跳转微信授权前记录显式勾选状态, 供回调页校验。 */
export function markLoginPolicyConsent(): void {
  getSessionStorage()?.setItem(LOGIN_POLICY_CONSENT_KEY, 'accepted')
}

/** 清理尚未消费的登录勾选状态。 */
export function clearLoginPolicyConsent(): void {
  getSessionStorage()?.removeItem(LOGIN_POLICY_CONSENT_KEY)
}

/** 微信授权回调消费显式勾选状态; 未从登录弹窗勾选进入时不应继续登录。 */
export function consumeLoginPolicyConsent(): boolean {
  const storage = getSessionStorage()
  if (!storage) return false
  const accepted = storage.getItem(LOGIN_POLICY_CONSENT_KEY) === 'accepted'
  storage.removeItem(LOGIN_POLICY_CONSENT_KEY)
  return accepted
}

/** 协议正文 (按 type) */
export function usePolicyContent(type: string | undefined) {
  return useQuery({
    queryKey: ['policy', 'content', POLICY_BIZ_TYPE, type],
    queryFn: () =>
      http.get<AppPolicyContentVo>(`/app/system/policy/${encodeURIComponent(type!)}`, {
        params: { bizType: POLICY_BIZ_TYPE },
      }),
    enabled: Boolean(type),
    staleTime: 30 * 60_000,
  })
}

/** 必选协议清单 + 当前用户同意状态 */
export function useRequiredPolicies(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['policy', 'required', POLICY_BIZ_TYPE],
    queryFn: () =>
      http.get<AppPolicyRequiredItemVo[]>('/app/system/policy/required', {
        params: { bizType: POLICY_BIZ_TYPE },
      }),
    enabled: opts.enabled ?? true,
    staleTime: 5 * 60_000,
    // 守卫层使用, 失败时静默返回空数组以免阻塞应用
    retry: 1,
  })
}

/**
 * 登录后自动提交全部待同意协议 (非 hook, 可在回调中直接调用).
 * 失败时静默处理, 不阻塞登录流程.
 */
export async function acceptAllRequiredPolicies(): Promise<void> {
  const policies = await http
    .get<AppPolicyRequiredItemVo[]>('/app/system/policy/required', {
      params: { bizType: POLICY_BIZ_TYPE },
    })
    .catch(() => [] as AppPolicyRequiredItemVo[])

  const pending = policies.filter((p) => p.required && !p.accepted && p.type && p.version)
  if (pending.length === 0) return

  const body: AppPolicyAcceptBo = {
    bizType: POLICY_BIZ_TYPE,
    items: pending.map((p) => ({ type: p.type!, version: p.version! })),
  }
  await http.post<void>('/app/system/policy/accept', body).catch(() => {})
}

/** 批量提交同意; items 为 { type, version }[] */
export function useAcceptPolicies() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['policy', 'accept'],
    mutationFn: async (items: AppPolicyAcceptItemBo[]) => {
      const body: AppPolicyAcceptBo = {
        bizType: POLICY_BIZ_TYPE,
        items,
      }
      return http.post<void>('/app/system/policy/accept', body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['policy', 'required'] })
    },
  })
}
